import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { frenchTranslations, FRENCH_SCHOOL_IDS } from '@/data/frenchTranslations';
import { getCurrentUser } from '@/data/users';

const LanguageContext = createContext({
  isFrench: false,
  t: (path, defaultValue) => defaultValue,
  language: 'en'
});

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }) => {
  // Use state to store current user so we can trigger re-renders
  const [currentUser, setCurrentUser] = useState(() => getCurrentUser());
  
  // Listen for localStorage changes and custom storage events
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'currentUser' || e.key === null) {
        setCurrentUser(getCurrentUser());
      }
    };
    
    // Listen for storage events from other tabs
    window.addEventListener('storage', handleStorageChange);
    
    // Listen for custom event when user logs in (same tab)
    const handleUserChange = () => {
      setCurrentUser(getCurrentUser());
    };
    window.addEventListener('userLoggedIn', handleUserChange);
    
    // Also check periodically in case of edge cases
    const checkUser = () => {
      const storedUser = getCurrentUser();
      if (JSON.stringify(storedUser) !== JSON.stringify(currentUser)) {
        setCurrentUser(storedUser);
      }
    };
    
    // Check on focus (when user comes back to the app)
    window.addEventListener('focus', checkUser);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLoggedIn', handleUserChange);
      window.removeEventListener('focus', checkUser);
    };
  }, [currentUser]);
  
  const isFrench = useMemo(() => {
    // Check if user's school is any of the French schools
    return currentUser?.school_id && FRENCH_SCHOOL_IDS.includes(currentUser.school_id);
  }, [currentUser?.school_id]);

  const language = isFrench ? 'fr' : 'en';

  // Translation function
  const t = (path, defaultValue) => {
    if (!isFrench) {
      return defaultValue;
    }
    
    const keys = path.split('.');
    let value = frenchTranslations;
    
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return defaultValue;
      }
    }
    
    return value || defaultValue;
  };

  const value = useMemo(() => ({
    isFrench,
    t,
    language
  }), [isFrench, language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
